import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Tooltip,
  Divider, Checkbox, FormControlLabel, Skeleton, LinearProgress
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Edit, ArrowBack, Add, Close, Save,
  Refresh, FileDownload, UploadFile, InfoOutlined
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';

// ─────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────
const CHART_TYPES = [
  { value: 'FAT_SNF_STEPS', label: 'Increase per fat/snf points' },
  { value: 'EXCEL',         label: 'Excel' },
];
const MILK_TABS = [
  { id: 1, label: 'Cow',     icon: '🐄' },
  { id: 2, label: 'Buffalo', icon: '🐃' },
];
const EMPTY_FORM = {
  chartType:      'FAT_SNF_STEPS',
  startingAmount: '',
  adjustment:     '0',
  fatSteps:       [],
  snfSteps:       [],
  excelFile:      null,
};
const EMPTY_STEP = { open: false, value: '', stepAmount: '', addAnother: false };

// ─────────────────────────────────────────────────────
//  GRID CALCULATION (FAT/SNF steps mode)
// ─────────────────────────────────────────────────────
function calcInc(target, steps) {
  let inc = 0;
  for (let i = 0; i < steps.length - 1; i++) {
    const { value: from, stepAmount: amt } = steps[i];
    const { value: to }                    = steps[i + 1];
    if (target <= from) break;
    const eff   = Math.min(target, to);
    const count = Math.round((eff - from) * 10);
    inc        += count * amt;
  }
  return Math.round(inc * 10000) / 10000;
}
function genRange(steps) {
  if (!steps || steps.length < 2) return [];
  const sorted = [...steps].sort((a, b) => a.value - b.value);
  const range  = [];
  for (let v = sorted[0].value; v <= sorted[sorted.length-1].value + 0.00001;
       v = Math.round((v + 0.1) * 10) / 10) {
    range.push(Math.round(v * 10) / 10);
  }
  return range;
}
function computeStepsGrid(chart) {
  try {
    const fat = (chart.fatSteps || [])
      .map(s => ({ value: parseFloat(s.fatValue ?? s.value ?? 0), stepAmount: parseFloat(s.stepAmount ?? 0) }))
      .sort((a, b) => a.value - b.value);
    const snf = (chart.snfSteps || [])
      .map(s => ({ value: parseFloat(s.snfValue ?? s.value ?? 0), stepAmount: parseFloat(s.stepAmount ?? 0) }))
      .sort((a, b) => a.value - b.value);
    if (fat.length < 2 || snf.length < 2) return null;
    const fatVals = genRange(fat);
    const snfVals = genRange(snf);
    const base    = parseFloat(chart.startingAmount || 0);
    const adj     = parseFloat(chart.adjustmentValue ?? 0);
    const grid    = {};
    for (const fv of fatVals) {
      grid[fv] = {};
      const fi = calcInc(fv, fat);
      for (const sv of snfVals) {
        grid[fv][sv] = parseFloat((base + fi + calcInc(sv, snf) + adj).toFixed(2));
      }
    }
    return { fatVals, snfVals: snfVals, grid };
  } catch { return null; }
}

// ─────────────────────────────────────────────────────
//  PARSE EXCEL GRID JSON (from DB)
// ─────────────────────────────────────────────────────
function parseGridJson(gridJson) {
  try {
    if (!gridJson) return null;
    const data = typeof gridJson === 'string' ? JSON.parse(gridJson) : gridJson;
    const colHeaders = data.colHeaders || [];
    const rows       = data.rows       || {};
    if (!colHeaders.length || !Object.keys(rows).length) return null;
    const fatVals = Object.keys(rows).map(parseFloat).sort((a, b) => a - b);
    const snfVals = colHeaders.map(parseFloat);
    const grid    = {};
    for (const fv of fatVals) {
      grid[fv] = {};
      const key = fv.toFixed(1);
      for (const sv of snfVals) {
        grid[fv][sv] = parseFloat(rows[key]?.[sv.toFixed(1)] ?? 0);
      }
    }
    return { fatVals, snfVals, grid };
  } catch { return null; }
}

// ─────────────────────────────────────────────────────
//  STEP DIALOG
// ─────────────────────────────────────────────────────
function StepDialog({ open, title, value, stepAmount, addAnother, onChange, onApply, onClose }) {
  const { theme } = useApp();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        py: 1.5, fontWeight: 700, borderBottom: '1px solid #f0f0f0'
      }}>
        {title}
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <TextField label="Step" value={value}
          onChange={(e) => onChange('value', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onApply()}
          type="number" fullWidth size="small" autoFocus
          inputProps={{ step: '0.1' }} sx={{ mb: 2 }} />
        <TextField label="Step Amount" value={stepAmount}
          onChange={(e) => onChange('stepAmount', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onApply()}
          type="number" fullWidth size="small"
          inputProps={{ step: '0.01' }} sx={{ mb: 1.5 }} />
        <FormControlLabel
          control={<Checkbox checked={addAnother}
            onChange={(e) => onChange('addAnother', e.target.checked)}
            size="small" sx={{ color:theme.primary,'&.Mui-checked':{color:theme.primary} }} />}
          label={<Typography variant="body2">Save And Add Another</Typography>} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ borderRadius: 2, color: '#555', borderColor: '#ddd' }}>Cancel</Button>
        <Button onClick={onApply} variant="contained" sx={{
          background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
          borderRadius: 2, fontWeight: 700,
          '&:hover': { background: `linear-gradient(135deg,${theme.dark},${theme.primary})` }
        }}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────
//  STEPS TABLE
// ─────────────────────────────────────────────────────
function StepsTable({ steps, label, onAdd, onRemove }) {
  const { theme } = useApp();
  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        mb:1, pb:0.8, borderBottom:'2px solid #f0fdf4' }}>
        <Typography variant="subtitle2" sx={{
          fontWeight:800, color:theme.primary, textTransform:'uppercase',
          letterSpacing:0.5, fontSize:'0.75rem' }}>
          {label} Steps
        </Typography>
        <Button size="small" onClick={onAdd}
          startIcon={<Add sx={{ fontSize:'14px !important' }} />}
          sx={{ color:theme.primary, fontWeight:700, fontSize:'0.75rem',
            minWidth:0, py:0.3, px:1, '&:hover':{ background:theme.light } }}>
          Add
        </Button>
      </Box>
      {steps.length === 0 ? (
        <Box sx={{ border:'1.5px dashed #d1fae5', borderRadius:2, py:2.5, textAlign:'center' }}>
          <Typography variant="caption" sx={{ color:'#bbb' }}>
            No {label} steps yet
          </Typography>
        </Box>
      ) : (
        <Box sx={{ border:'1px solid #e8f5e9', borderRadius:2, overflow:'hidden' }}>
          <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr 28px',
            background:theme.light, px:1.5, py:0.6, borderBottom:'1px solid #e8f5e9' }}>
            <Typography variant="caption" sx={{ fontWeight:800, color:'#555' }}>Step</Typography>
            <Typography variant="caption" sx={{ fontWeight:800, color:'#555' }}>Amount</Typography>
            <Box />
          </Box>
          {steps.map((s, idx) => (
            <Box key={s.id} sx={{
              display:'grid', gridTemplateColumns:'1fr 1fr 28px',
              px:1.5, py:0.7, alignItems:'center',
              borderTop: idx===0?'none':'1px solid #f0fdf4',
              background: idx%2===0?'#fff':'#fafffe',
              '&:hover':{ background:theme.light } }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:theme.primary }}>
                {parseFloat(s.value).toFixed(1)}
              </Typography>
              <Typography variant="body2" sx={{ color:'#444' }}>
                {parseFloat(s.stepAmount||0).toFixed(2)}
              </Typography>
              <IconButton size="small" onClick={() => onRemove(s.id)}
                sx={{ color:'#e53e3e', p:0.2 }}>
                <Close sx={{ fontSize:13 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────
//  RATE GRID TABLE (shared by both modes)
// ─────────────────────────────────────────────────────
function RateGrid({ computedGrid }) {
  const { theme } = useApp();
  if (!computedGrid) return null;
  const { fatVals, snfVals, grid } = computedGrid;
  return (
    <Box sx={{ overflowX:'auto', overflowY:'auto', maxHeight:'65vh' }}>
      <table style={{ borderCollapse:'collapse', minWidth:'max-content' }}>
        <thead>
          <tr>
            <th style={{
              position:'sticky', left:0, top:0, zIndex:4,
              background:theme.primary, color:'#fff', fontWeight:800,
              fontSize:'0.72rem', padding:'8px 12px', minWidth:70,
              borderRight:'2px solid #145e38', borderBottom:'2px solid #145e38'
            }}>fat_snf</th>
            {snfVals.map(sv => (
              <th key={sv} style={{
                position:'sticky', top:0, zIndex:3,
                background:theme.primary, color:'#fff', fontWeight:700,
                fontSize:'0.7rem', padding:'8px 8px', minWidth:66,
                textAlign:'center', borderLeft:'1px solid #1d8c52',
                borderBottom:'2px solid #145e38', whiteSpace:'nowrap'
              }}>{sv.toFixed(1)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fatVals.map((fv, ri) => (
            <tr key={fv} style={{ background: ri%2===0?'#fff':'#f8fffe' }}>
              <td style={{
                position:'sticky', left:0, zIndex:2,
                background: ri%2===0?theme.light:'#e6f7ef',
                fontWeight:700, color:theme.primary, fontSize:'0.72rem',
                padding:'5px 12px', borderRight:'2px solid #d1fae5',
                borderBottom:'1px solid #f0f0f0', whiteSpace:'nowrap'
              }}>{fv.toFixed(1)}</td>
              {snfVals.map(sv => (
                <td key={sv} style={{
                  textAlign:'center', fontSize:'0.72rem', padding:'5px 8px',
                  color:'#333', borderLeft:'1px solid #f5f5f5',
                  borderBottom:'1px solid #f5f5f5', whiteSpace:'nowrap'
                }}>
                  {grid[fv]?.[sv]?.toFixed(2) ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

// ─────────────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────────────
export default function RateChart() {
  const { theme } = useApp();
  const [activeTab,    setActiveTab]    = useState(1);
  const [mode,         setMode]         = useState('view');
  const [charts,       setCharts]       = useState({ 1: null, 2: null });
  const [loadingTab,   setLoadingTab]   = useState({ 1: true, 2: true });
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [fatDialog,    setFatDialog]    = useState(EMPTY_STEP);
  const [snfDialog,    setSnfDialog]    = useState(EMPTY_STEP);
  const [saving,       setSaving]       = useState(false);
  const [importing,    setImporting]    = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [uploadProg,   setUploadProg]   = useState(0);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const fileRef = useRef(null);

  const currentChart  = charts[activeTab];
  const milkTab       = MILK_TABS.find(t => t.id === activeTab) || MILK_TABS[0];

  // ── Compute grid depending on chart type ────────────
  const computedGrid = (() => {
    if (!currentChart) return null;
    if (currentChart.chartType === 'EXCEL') {
      return parseGridJson(currentChart.gridJson);
    }
    return computeStepsGrid(currentChart);
  })();

  const btnSx = {
    background:  `linear-gradient(135deg,${theme.primary},${theme.hover})`,
    borderRadius: 2, fontWeight: 700,
    '&:hover':   { background:`linear-gradient(135deg,${theme.dark},${theme.primary})` }
  };

  // ── Fetch ────────────────────────────────────────────
  const fetchChart = useCallback(async (milkTypeId) => {
    setLoadingTab(prev => ({ ...prev, [milkTypeId]: true }));
    try {
      const res = await api.get(`/ratechart/${milkTypeId}`);
      setCharts(prev => ({
        ...prev,
        [milkTypeId]: res.data.success && res.data.data ? res.data.data : null
      }));
    } catch {
      setCharts(prev => ({ ...prev, [milkTypeId]: null }));
    } finally {
      setLoadingTab(prev => ({ ...prev, [milkTypeId]: false }));
    }
  }, []);

  useEffect(() => { fetchChart(1); fetchChart(2); }, [fetchChart]);

  // ── Enter Edit ───────────────────────────────────────
  const handleEdit = () => {
    const chart = charts[activeTab];
    if (chart) {
      setForm({
        chartType:      chart.chartType || 'FAT_SNF_STEPS',
        startingAmount: String(chart.startingAmount ?? ''),
        adjustment:     String(chart.adjustmentValue ?? '0'),
        fatSteps:       (chart.fatSteps || []).map(s => ({
          id:         s.stepId ?? Math.random(),
          value:      String(s.fatValue ?? ''),
          stepAmount: String(s.stepAmount ?? '0'),
        })),
        snfSteps:       (chart.snfSteps || []).map(s => ({
          id:         s.stepId ?? Math.random(),
          value:      String(s.snfValue ?? ''),
          stepAmount: String(s.stepAmount ?? '0'),
        })),
        excelFile: null,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError('');
    setMode('edit');
  };

  const handleTabSwitch = (id) => {
    setActiveTab(id); setMode('view'); setError(''); setSuccess('');
  };
  const setField = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  // ── Add FAT/SNF steps ────────────────────────────────
  const addStep = (type, dialog, setDialog) => {
    if (!dialog.value) return;
    const key  = type === 'fat' ? 'fatSteps' : 'snfSteps';
    const step = { id: Date.now()+Math.random(), value: dialog.value, stepAmount: dialog.stepAmount||'0' };
    setForm(prev => ({
      ...prev,
      [key]: [...prev[key], step].sort((a,b) => parseFloat(a.value)-parseFloat(b.value))
    }));
    if (dialog.addAnother) setDialog(prev => ({ ...prev, value:'', stepAmount:'' }));
    else                   setDialog(EMPTY_STEP);
  };

  const removeStep = (type, id) => {
    const key = type === 'fat' ? 'fatSteps' : 'snfSteps';
    setForm(prev => ({ ...prev, [key]: prev[key].filter(s => s.id !== id) }));
  };

  // ── Download demo ────────────────────────────────────
  const handleDownloadDemo = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/ratechart/demo/${activeTab}`, {
        responseType: 'blob'
      });
      // If server returned JSON error instead of file, parse and show it
      if (res.data?.type === 'application/json' || res.headers?.['content-type']?.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        setError(json.message || 'Failed to download demo file.');
        return;
      }
      const fileName = activeTab === 2
        ? 'DairyPro_Buffalo_RateChart_Demo.xlsx'
        : 'DairyPro_Cow_RateChart_Demo.xlsx';
      const url  = window.URL.createObjectURL(new Blob([res.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href  = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to download demo file: ' + (e.message || ''));
    } finally {
      setDownloading(false);
    }
  };

  // ── Import Excel ────────────────────────────────────
  const handleImport = async (file) => {
    if (!file) return;
    setError('');
    setImporting(true);
    setUploadProg(10);

    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploadProg(40);
      const res = await api.post(`/ratechart/import/${activeTab}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 60) / e.total);
          setUploadProg(40 + pct);
        }
      });
      setUploadProg(100);

      if (res.data.success) {
        setSuccess(`${milkTab.label} rate chart imported successfully!`);
        await fetchChart(activeTab);
        setMode('view');
        setTimeout(() => setSuccess(''), 3500);
      } else {
        setError(res.data.message || 'Import failed.');
      }
    } catch {
      setError('Import failed. Please check file format.');
    } finally {
      setImporting(false);
      setUploadProg(0);
      setField('excelFile', null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ── Save (steps mode) ────────────────────────────────
  const handleSave = async () => {
    setError('');
    if (!form.startingAmount) { setError('Please enter a starting amount.'); return; }
    if (form.fatSteps.length < 2) { setError('Add at least 2 FAT steps.'); return; }
    if (form.snfSteps.length < 2) { setError('Add at least 2 SNF steps.'); return; }

    setSaving(true);
    try {
      const res = await api.post('/ratechart', {
        milkTypeId:      activeTab,
        chartType:       'FAT_SNF_STEPS',
        startingAmount:  parseFloat(form.startingAmount) || 0,
        adjustmentValue: parseFloat(form.adjustment)     || 0,
        fatSteps: form.fatSteps.map(s => ({
          fatValue: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) })),
        snfSteps: form.snfSteps.map(s => ({
          snfValue: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) })),
      });
      if (res.data.success) {
        setSuccess(`${milkTab.label} rate chart saved!`);
        await fetchChart(activeTab);
        setMode('view');
        setTimeout(() => setSuccess(''), 3500);
      } else {
        setError(res.data.message || 'Something went wrong.');
      }
    } catch {
      setError('Cannot connect to server.');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────
  return (
    <Layout title="Rate Chart">

      {success && (
        <Alert severity="success" sx={{ mb:2, borderRadius:2 }}
          onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Card sx={{ borderRadius:3, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>

        {/* ── Header ── */}
        <Box sx={{ px:2.5, py:1.8, display:'flex', alignItems:'center',
          justifyContent:'space-between', borderBottom:'1px solid #f0f0f0' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            {mode === 'edit' && (
              <Tooltip title="Back">
                <IconButton size="small"
                  onClick={() => { setMode('view'); setError(''); }}
                  sx={{ color:theme.primary, mr:0.5 }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <BarChartIcon sx={{ color:theme.primary, fontSize:24 }} />
            <Typography variant="h6" sx={{ fontWeight:800 }}>
              {mode === 'edit' ? 'Edit Rate Chart' : 'Rate Chart'}
            </Typography>
          </Box>
          {mode === 'view' && (
            <Box sx={{ display:'flex', gap:1 }}>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => fetchChart(activeTab)}
                  sx={{ border:'1px solid #e0e0e0', borderRadius:1.5, color:theme.primary }}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" size="small" startIcon={<Edit fontSize="small" />}
                onClick={handleEdit}
                sx={{ borderRadius:2, borderColor:theme.primary, color:theme.primary, fontWeight:700,
                  '&:hover':{ background:theme.light } }}>
                Change
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Milk Tabs ── */}
        <Box sx={{ px:2.5, py:1.2, display:'flex', alignItems:'center', gap:1,
          borderBottom:'1px solid #f0f0f0', background:'#fafffe' }}>
          <Typography variant="body2" sx={{ fontWeight:700, color:'#888', mr:0.5 }}>
            Milk:
          </Typography>
          {MILK_TABS.map(tab => (
            <Button key={tab.id} size="small"
              variant={activeTab===tab.id ? 'contained' : 'outlined'}
              onClick={() => handleTabSwitch(tab.id)}
              sx={{
                borderRadius:2, fontWeight:700, fontSize:'0.82rem', px:2,
                background: activeTab===tab.id
                  ? `linear-gradient(135deg,${theme.primary},${theme.hover})` : 'transparent',
                borderColor:theme.primary,
                color: activeTab===tab.id ? '#fff' : theme.primary,
                '&:hover': {
                  background: activeTab===tab.id
                    ? `linear-gradient(135deg,${theme.dark},${theme.primary})` : theme.light
                }
              }}>
              {tab.icon} {tab.label}
            </Button>
          ))}
          {mode === 'view' && currentChart && (
            <Typography variant="body2" sx={{
              ml:1.5, fontWeight:700, color:theme.primary, fontSize:'0.82rem'
            }}>
              {milkTab.label} Rate Chart
            </Typography>
          )}
        </Box>

        {/* ══════════════════════════════════════════════
            VIEW MODE
        ══════════════════════════════════════════════ */}
        {mode === 'view' && (
          <>
            {loadingTab[activeTab] ? (
              <Box sx={{ p:2.5 }}>
                {Array.from({length:8}).map((_,i) => (
                  <Skeleton key={i} height={30} sx={{ mb:0.5, borderRadius:1 }} />
                ))}
              </Box>
            ) : !currentChart || !computedGrid ? (
              <Box sx={{ textAlign:'center', py:10 }}>
                <BarChartIcon sx={{ fontSize:72, color:'#e0e0e0', mb:2 }} />
                <Typography variant="h6" sx={{ color:'#bbb', mb:1 }}>
                  No {milkTab.label} rate chart yet
                </Typography>
                <Typography variant="body2" sx={{ color:'#ccc', mb:3 }}>
                  Click "Change" to create one.
                </Typography>
                <Button variant="contained" startIcon={<Add />}
                  onClick={handleEdit} sx={btnSx}>
                  Create Rate Chart
                </Button>
              </Box>
            ) : (
              <>
                {/* Info bar */}
                <Box sx={{ px:2.5, py:1, display:'flex', gap:3, alignItems:'center',
                  flexWrap:'wrap', background:'#f8fffe', borderBottom:'1px solid #f0f0f0' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color:'#888' }}>Type</Typography>
                    <Typography variant="body2" sx={{ fontWeight:700 }}>
                      {currentChart.chartType==='EXCEL'
                        ? '📊 Excel Import'
                        : 'Increase per fat/snf points'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color:'#888' }}>Starting Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight:700, color:theme.primary }}>
                      ₹{parseFloat(currentChart.startingAmount||0).toFixed(2)}
                    </Typography>
                  </Box>
                  {parseFloat(currentChart.adjustmentValue||0) !== 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color:'#888' }}>Adjustment</Typography>
                      <Typography variant="body2" sx={{ fontWeight:700, color:'#f97316' }}>
                        {parseFloat(currentChart.adjustmentValue)>0?'+':''}
                        {parseFloat(currentChart.adjustmentValue).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" sx={{ color:'#888' }}>Grid</Typography>
                    <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>
                      {computedGrid.fatVals.length} × {computedGrid.snfVals.length}
                    </Typography>
                  </Box>
                </Box>

                <RateGrid computedGrid={computedGrid} />
              </>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════
            EDIT MODE
        ══════════════════════════════════════════════ */}
        {mode === 'edit' && (
          <Box sx={{ p:3 }}>

            {error && (
              <Alert severity="error" sx={{ mb:2.5, borderRadius:2 }}
                onClose={() => setError('')}>{error}</Alert>
            )}

            {/* Category / Milk info */}
            <Box sx={{ display:'flex', gap:4, mb:2.5, p:2,
              background:'#f8fffe', borderRadius:2, border:'1px solid #d1fae5' }}>
              <Box>
                <Typography variant="caption" sx={{ color:'#888', fontWeight:600 }}>
                  Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight:700 }}>Collection</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color:'#888', fontWeight:600 }}>
                  Milk Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight:700 }}>
                  {milkTab.icon} {milkTab.label}
                </Typography>
              </Box>
            </Box>

            {/* Chart Type */}
            <FormControl size="small" sx={{ mb:2.5, minWidth:300 }}>
              <InputLabel>Type</InputLabel>
              <Select value={form.chartType} label="Type"
                onChange={(e) => setField('chartType', e.target.value)}>
                {CHART_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ══ EXCEL MODE ══ */}
            {form.chartType === 'EXCEL' && (
              <Box>
                {/* Info box */}
                <Alert severity="info" icon={<InfoOutlined />}
                  sx={{ mb:2.5, borderRadius:2, fontSize:'0.82rem' }}>
                  Download the demo Excel file, modify the rate values, then upload it here.
                  Do not change the file structure (row/column layout).
                </Alert>

                {/* Download demo button */}
                <Button
                  variant="outlined"
                  startIcon={downloading
                    ? <CircularProgress size={16} /> : <FileDownload />}
                  onClick={handleDownloadDemo}
                  disabled={downloading}
                  sx={{
                    mb:2.5, borderRadius:2,
                    borderColor:theme.primary, color:theme.primary, fontWeight:700,
                    '&:hover':{ background:theme.light }
                  }}>
                  {downloading ? 'Downloading...' : `Download ${milkTab.label} Demo Excel`}
                </Button>

                {/* Upload area */}
                <Box sx={{ mb:2.5 }}>
                  <Typography variant="body2" sx={{ fontWeight:700, mb:1, color:'#555' }}>
                    Import Excel
                  </Typography>

                  <Box
                    onClick={() => !importing && fileRef.current?.click()}
                    sx={{
                      border: form.excelFile
                        ? '2px solid #1a7a4a' : '2px dashed #d1fae5',
                      borderRadius:2, p:3, maxWidth:480,
                      textAlign:'center', cursor: importing?'default':'pointer',
                      background: form.excelFile?theme.light:'#fafffe',
                      transition:'all 0.2s',
                      '&:hover': !importing ? { borderColor:theme.primary, background:theme.light } : {}
                    }}>
                    <UploadFile sx={{ fontSize:36, color:theme.primary, mb:1, opacity:0.7 }} />
                    <Typography variant="body2" sx={{ fontWeight:700, color:theme.primary }}>
                      {form.excelFile ? `✅ ${form.excelFile.name}` : 'Click to browse Excel file'}
                    </Typography>
                    <Typography variant="caption" sx={{ color:'#aaa' }}>
                      .xlsx or .xls only
                    </Typography>
                  </Box>

                  <input
                    ref={fileRef} type="file" accept=".xlsx,.xls" hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setField('excelFile', f);
                    }}
                  />

                  {/* Upload progress */}
                  {importing && (
                    <Box sx={{ mt:2, maxWidth:480 }}>
                      <LinearProgress variant="determinate" value={uploadProg}
                        sx={{
                          height:8, borderRadius:4,
                          '& .MuiLinearProgress-bar': { background:theme.primary }
                        }} />
                      <Typography variant="caption" sx={{ color:theme.primary, fontWeight:700 }}>
                        Importing... {uploadProg}%
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Adjustment */}
                <TextField
                  label="Adjustment (+/-)" value={form.adjustment}
                  onChange={(e) => setField('adjustment', e.target.value)}
                  size="small" type="number" inputProps={{ step:'0.01' }}
                  sx={{ maxWidth:200, mb:2.5 }}
                />

                <Divider sx={{ mb:2 }} />
                <Box sx={{ display:'flex', gap:2 }}>
                  <Button onClick={() => setField('excelFile', null)} variant="outlined"
                    sx={{ borderRadius:2, color:'#888', borderColor:'#ddd' }}>
                    Clear
                  </Button>
                  <Button
                    onClick={() => handleImport(form.excelFile)}
                    variant="contained" disabled={!form.excelFile || importing}
                    startIcon={importing
                      ? <CircularProgress size={16} color="inherit" />
                      : <UploadFile />}
                    sx={btnSx}>
                    {importing ? 'Importing...' : 'Import & Save'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ══ FAT/SNF STEPS MODE ══ */}
            {form.chartType === 'FAT_SNF_STEPS' && (
              <>
                <TextField
                  label="Starting Amount" value={form.startingAmount}
                  onChange={(e) => setField('startingAmount', e.target.value)}
                  size="small" type="number" inputProps={{ step:'0.5' }}
                  sx={{ mb:2.5, maxWidth:200 }}
                />

                <Box sx={{ display:'grid',
                  gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr' },
                  gap:3, mb:2.5 }}>
                  <StepsTable steps={form.fatSteps} label="FAT"
                    onAdd={() => setFatDialog({ ...EMPTY_STEP, open:true })}
                    onRemove={(id) => removeStep('fat', id)} />
                  <StepsTable steps={form.snfSteps} label="SNF"
                    onAdd={() => setSnfDialog({ ...EMPTY_STEP, open:true })}
                    onRemove={(id) => removeStep('snf', id)} />
                </Box>

                <TextField
                  label="Adjustment (+/-)" value={form.adjustment}
                  onChange={(e) => setField('adjustment', e.target.value)}
                  size="small" type="number" inputProps={{ step:'0.01' }}
                  sx={{ maxWidth:200, mb:2.5 }}
                />

                {/* Live preview */}
                {form.fatSteps.length >= 2 && form.snfSteps.length >= 2 && form.startingAmount && (
                  <Box sx={{ p:1.5, background:theme.light, borderRadius:2,
                    border:'1px solid #d1fae5', mb:2.5 }}>
                    <Typography variant="caption" sx={{ color:theme.primary, fontWeight:700 }}>
                      ✅ Grid will be{' '}
                      {genRange(form.fatSteps.map(s=>({value:parseFloat(s.value),stepAmount:0}))).length}
                      {' '}rows ×{' '}
                      {genRange(form.snfSteps.map(s=>({value:parseFloat(s.value),stepAmount:0}))).length}
                      {' '}columns
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ mb:2 }} />
                <Box sx={{ display:'flex', gap:2 }}>
                  <Button onClick={handleEdit} variant="outlined"
                    sx={{ borderRadius:2, color:'#888', borderColor:'#ddd' }}>
                    Reset
                  </Button>
                  <Button onClick={handleSave} variant="contained" disabled={saving}
                    startIcon={saving
                      ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    sx={btnSx}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </>
            )}

          </Box>
        )}

      </Card>

      {/* FAT Dialog */}
      <StepDialog open={fatDialog.open} title="FAT steps"
        value={fatDialog.value} stepAmount={fatDialog.stepAmount}
        addAnother={fatDialog.addAnother}
        onChange={(f,v) => setFatDialog(p=>({...p,[f]:v}))}
        onApply={() => addStep('fat', fatDialog, setFatDialog)}
        onClose={() => setFatDialog(EMPTY_STEP)} />

      {/* SNF Dialog */}
      <StepDialog open={snfDialog.open} title="SNF steps"
        value={snfDialog.value} stepAmount={snfDialog.stepAmount}
        addAnother={snfDialog.addAnother}
        onChange={(f,v) => setSnfDialog(p=>({...p,[f]:v}))}
        onApply={() => addStep('snf', snfDialog, setSnfDialog)}
        onClose={() => setSnfDialog(EMPTY_STEP)} />

    </Layout>
  );
}
